/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.currentRedirects = null;
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
      this._searchForm = this.querySelector('form');
      this.categoriesExpanded = false;

      document.querySelectorAll('.hr-filters-toggle-mobile').forEach((btn) => {
        btn.addEventListener('click', this._toggleMobileFilters.bind(this));
      });
      document.querySelectorAll('.hr-search-title-mobile').forEach((title) => {
        title.addEventListener('click', () => {
          let dataToggle = title.getAttribute('data-toggle');
          document.querySelectorAll('.hr-toggle-mobile, .hr-search-title-mobile').forEach((toggle) => {
            toggle.classList.remove('active');
          });
          title.classList.toggle('active');
          document.querySelectorAll(`.hr-toggle-mobile[data-toggle="${dataToggle}"]`).forEach((toggle) => {
            toggle.classList.add('active');
          });
        });
      });

      // Add event listener for mobile category and page show more button
      const self = this;
      document.addEventListener('click', (e) => {
        if (e.target.closest('.hr-categories-show-more-btn')) {
          self._toggleMobileCategories('categories');
        }
        if (e.target.closest('.hr-pages-show-more-btn')) {
          self._toggleMobileCategories('pages');
        }
      });
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      if (window.theme.settings.enableHelloRetail && window.theme.settings.hrQuickSearchKey) {
        this.getResultsFromHelloRetail(searchTerm);
      }
      else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        if (window.theme.settings.enableHelloRetail && window.theme.settings.hrQuickSearchKey) {
          this.getResultsFromHelloRetail(query);
        }
        else {
          this.getResults(query);
        }
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        if (window.theme.settings.enableHelloRetail && window.theme.settings.hrQuickSearchKey) {
          this.getResultsFromHelloRetail(searchTerm);
        }
        else {
          this.getResults(searchTerm);
        }
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          // If redirects exist from Hello Retail, navigate to that URL
          if (this.currentRedirects && this.currentRedirects.length > 0) {
            evt.preventDefault();
            window.location.href = this.currentRedirects[0].url;
            return;
          }
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();

      let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      let searchParams = '';
      if (this.productTypeInput && this.productTypeInput.value !== '') {
        searchParams = `q=product_type:${encodeURIComponent(this.productTypeInput.value)} AND ${encodeURIComponent(searchTerm)}`;
      } else {
        searchParams = `q=${encodeURIComponent(searchTerm)}`;
      }

      searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
        theme.settings.pSearchLimitScope
      }`;
      searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      searchParams += '&section_id=predictive-search';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      try {
        const response = await fetch(`${theme.routes.predictiveSearch}?${searchParams}`);
        if (!response.ok) throw new Error(response.status);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
        const resultsMarkup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

        this.cachedResults[queryKey] = resultsMarkup;
        this.renderResults(resultsMarkup);
      } catch (error) {
        this.close();
        throw error;
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      console.log(resultsMarkup);
      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // Clear redirects when closing search
      this.currentRedirects = null;

      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
    }

    async getResultsFromHelloRetail(searchTerm) {
      let self = this;
      var options = {
          format: "json",
          return_filters: true,
          filters: [],
          sorting: [],
          engine_options: {
            product: {
              current_count: window.theme.settings.hrProductNumber,
              start: 0
            },
            category: {
              current_count: 10,
              start: 0
            },
            site_page: {
              current_count: 5,
              start: 0
            },
            blog_post: {
              current_count: 5,
              start: 0
            },
            brand: {
              current_count: 0,
              start: 0
            },
            redirects: {
              current_count: 2,
              start: 0
            }
          }
      };

      // ignore all unlisted products from HR results
      options.filters.push(`extraData.unlisted:false`);

      console.log("options", options);

      this.currentSearchSort = [];

      // loop all filter item and add selected filter to query params
      let form = this._searchForm;
      form.querySelectorAll('.hr-filter-dropdown-content li.selected:not(.hr-search-sort-option)').forEach(function (item) {
        options.filters.push(item.getAttribute('data-query'));
      });

      // add price param to query params
      if (form.querySelector('.hr-filter-dropdown-content .field-price-min') && form.querySelector('.hr-filter-dropdown-content .field-price-max')) {
        let priceMin = parseInt(form.querySelector('.field-price-min').getAttribute('min'));
        let priceMax = parseInt(form.querySelector('.field-price-max').getAttribute('max'));
        let priceMinSelected = form.querySelector('.field-price-min').value;
        let priceMaxSelected = form.querySelector('.field-price-max').value;
        if (priceMinSelected > priceMin || priceMaxSelected < priceMax) {
          options.filters.push(`price:${priceMinSelected},${priceMaxSelected}`);
        }
      }

      // add sort param to query params
      if (form.querySelector('.hr-search-sort-option.selected')) {
        let sortOption = form.querySelector('.hr-search-sort-option.selected').getAttribute('data-value');
        if (sortOption != 'most-popular') {
          options.sorting.push(sortOption);
          this.currentSearchSort.push(sortOption);
        }
      }
      
      hrq = window.hrq || [];
      hrq.push([
          "search",
          window.theme.settings.hrQuickSearchKey,
          searchTerm,
          options,
          function(response) {
            // Handle redirects - store them for Enter key navigation
            let redirects = response.redirects;
            console.log("Redirects Result", response.redirects);
            // Store redirects for use when user presses Enter
            self.currentRedirects = (redirects && redirects.length > 0) ? redirects : null;
            
            let categories = response.categories;
            let articles = response.blog_posts;
            let products = response.result;
            let pages = response.site_pages;
            self.generateResultHtml(response, categories, articles, products, pages);
            // self.renderResults(newResultHtml);
          }
      ]);
    }

    generateResultHtml(response, categories, articles, products, pages) {
      let form = this._searchForm;
      let selector = {
        productListWrapper: '.hr-products .hr-product-list',
        categoriesWrapper: '.hr-categories',
        filtersWrapper: '.hr-filters',
        productSke: '.hr-product-grid-skeleton product-card',
        collectionSke: '.hr-collection-skeleton li',
        filterSke: '.hr-filter-skeleton li',
        priceSke: '.hr-price-skeleton',
        productEmpty: '.hr-products-empty',
        categoryEmpty: '.hr-categories-empty',
        categoryShowMoreWrapper: '.hr-categories-show-more',
        categoryShowMoreBtn: '.hr-categories-show-more-btn',
        showMoreText: '.hr-show-more-text',
        showLessText: '.hr-show-less-text',
        pagesWrapper: '.hr-pages',
        pageShowMoreWrapper: '.hr-pages-show-more',
        pageShowMoreBtn: '.hr-pages-show-more-btn',
        pageEmpty: '.hr-pages-empty',
        pageShowMoreText: '.hr-show-more-text-pages',
        pageShowLessText: '.hr-show-less-text-pages'
      }
      this.selector = selector;
      form.querySelector('.hello-retail-search-results .hr-inner').classList.remove('hidden');
      form.querySelector('.hr-price-submit').classList.remove('loading');

      // 1. Get the product list from JSON data, then show on search grid
      this._renderSearchProducts(products);
      // 2. Iterate through categories and create list on left side
      let categoriesWrapper = form.querySelector(selector.categoriesWrapper);
      let showMoreWrapper = form.querySelector(selector.categoryShowMoreWrapper);
      categoriesWrapper.innerHTML = '';

      // Reset mobile categories state for new search
      this.categoriesExpanded = false;

      let countCategories = 0;
      let isMobile = window.innerWidth < 768;
      
      if (categories && categories.length) {
        form.querySelector(selector.categoryEmpty).classList.add('hidden');
        categories.forEach((category, index) => {
          let shouldShow = true;
          if (window.theme.settings.helloretailHideCollectionFromSearch) {
            let excludeCollections = window.theme.settings.helloretailHideCollectionFromSearch.split(',');
            excludeCollections.forEach(function (exclCollection) {
              if (exclCollection.indexOf('*') == 0) {
                let exclCollectionRegex = exclCollection.replace('*', '');
                if (category.title.toLowerCase().indexOf(exclCollectionRegex.toLowerCase()) > -1) {
                  shouldShow = false;
                }
              } else {
                if (exclCollection.toLowerCase() == category.title.toLowerCase()) {
                  shouldShow = false;
                }
              }
            });
          }
          if (shouldShow) {
            let categorySke = form.querySelector(selector.collectionSke).cloneNode(true);
            categorySke.querySelector('a').innerText = category.title;
            categorySke.querySelector('a').setAttribute('href', category.url);
            
            // On mobile, hide categories beyond the first 4
            // DO NOT need this anymore since we have changed the list layout to scrollable
            /*
            if (isMobile && countCategories >= 4) {
              console.log(`Hiding category ${countCategories} on initial render:`, category.title);
              categorySke.classList.add('hidden');
            }
            */
            
            categoriesWrapper.appendChild(categorySke);
            countCategories++;
          }
        });

        // Show/hide the "Show more" button based on mobile and number of categories
        if (isMobile && countCategories > 4) {
          // DO NOT need this anymore since we have changed the list layout to scrollable
          /*
          // console.log('Showing "Show more" button');
          showMoreWrapper.classList.remove('hidden');
          // Reset button text to "Show more"
          form.querySelector(selector.showMoreText).classList.remove('hidden');
          form.querySelector(selector.showLessText).classList.add('hidden');
          */
        } else {
          // console.log('Hiding "Show more" button');
          showMoreWrapper.classList.add('hidden');
        }

        if (countCategories == 0) {
          this._showSearchArea(this._searchForm, '.hr-categories-empty')
        }
      } else {
        showMoreWrapper.classList.add('hidden');
        this._showSearchArea(this._searchForm, '.hr-categories-empty')
      }

      // 3. render filters on top of product grid
      this._renderSearchFilters(response);
  
      // 4. render pages and articles below categories
      let pagesWrapper = form.querySelector(selector.pagesWrapper);
      let pageShowMoreWrapper = form.querySelector(selector.pageShowMoreWrapper);
      pagesWrapper.innerHTML = '';
      let countPages = 0;
      if (pages.length || articles.length) {
        form.querySelector(selector.pageEmpty).classList.add('hidden');
        pagesWrapper.innerHTML = '';
        pages.forEach((page, index) => {
          let pageSke = form.querySelector(selector.collectionSke).cloneNode(true);
          pageSke.querySelector('a').innerText = page.title.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
          pageSke.querySelector('a').setAttribute('href', page.url);
          // On mobile, hide categories beyond the first 4
          if (isMobile && countPages >= 4) {
            pageSke.classList.add('hidden');
          }
          countPages++;
          pagesWrapper.appendChild(pageSke);
        });
        articles.forEach((article, index) => {
          let pageSke = form.querySelector(selector.collectionSke).cloneNode(true);
          pageSke.querySelector('a').innerText = article.title.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
          pageSke.querySelector('a').setAttribute('href', article.url);
          // On mobile, hide categories beyond the first 4
          if (isMobile && countPages >= 4) {
            pageSke.classList.add('hidden');
          }
          countPages++;
          pagesWrapper.appendChild(pageSke);
        });

        // Show/hide the "Show more" button based on mobile and number of pages
        if (isMobile && countPages > 4) {
          pageShowMoreWrapper.classList.remove('hidden');
          form.querySelector(selector.pageShowMoreText).classList.remove('hidden');
          form.querySelector(selector.pageShowLessText).classList.add('hidden');
        } else {
          pageShowMoreWrapper.classList.add('hidden');
        }

        if (countPages == 0) {
          this._showSearchArea(this._searchForm, '.hr-pages-empty')
        }
      } else {
        pageShowMoreWrapper.classList.add('hidden');
        this._showSearchArea(this._searchForm, '.hr-pages-empty');
      }
      

      this.querySelector('.hello-retail-search-results')?.classList.remove('hidden');
      this.setAttribute('results', true);
      this.open();

      // set height for the .hr-right
      const hrLeftCollectionsHeight = this.querySelector('.hello-retail-search-results .hr-left-categories').offsetHeight;
      const hrLeftPagesHeight = this.querySelector('.hello-retail-search-results .hr-left-pages').offsetHeight;
      const hrLeftHeight = hrLeftCollectionsHeight + hrLeftPagesHeight;
      this.querySelector('.hello-retail-search-results .hr-right').style.minHeight = hrLeftHeight + 'px';

      // scroll search content to the top
      if (this.querySelector('.predictive-search__scroller')) {
        this.querySelector('.predictive-search__scroller').scrollTop = 0;
      }
    }

    /**
     * Render the search product results from Hello Retail
     */
    async _renderSearchProducts(products) {
      if (products && products.length) {
        this.numberOfPage = Math.ceil(products.length / 20);
      }
      let form = this._searchForm;
      let selector = this.selector;
      let productListWrapper = form.querySelector(selector.productListWrapper);
      let self = this;
      if (products && products.length) {
        // console.log('products', products);
        // hide empty message
        form.querySelector(selector.productEmpty).classList.add('hidden');
        form.querySelector('.hr-filters-toggle-mobile')?.classList.remove('hidden');

        // show product list wrapper
        // if (this.page == 1) {
        //   productListWrapper.innerHTML = '';
        // }
        productListWrapper.innerHTML = '';
        productListWrapper.classList.remove('hidden');
        // Iterate through products and add them to the list
        products.forEach(function(product) {
          let productSke = self.renderProductItem(product);
          let productLi = document.createElement('li');
          productLi.appendChild(productSke);
          productListWrapper.appendChild(productLi);
        });
        
      } else {
        // show empty message
        form.querySelector(selector.productEmpty).classList.remove('hidden');
        // hide product list wrapper
        productListWrapper.classList.add('hidden');
        form.querySelector('.hr-filters-toggle-mobile')?.classList.add('hidden');
      }
    }

    _showSearchArea(form, selector) {
      form.querySelector(selector).classList.remove('hidden');
      // switch to Product tab
      form.querySelector('.hr-search-title-mobile[data-toggle="left"]')?.classList.remove('active');
      form.querySelector('.hr-search-title-mobile[data-toggle="right"]')?.classList.add('active');
  
      form.querySelector('.hr-toggle-mobile[data-toggle="left"]')?.classList.remove('active');
      form.querySelector('.hr-toggle-mobile[data-toggle="right"]')?.classList.add('active');
    }

    renderProductItem(product) {
      let self = this;
      let form = this._searchForm;
      let selector = this.selector;
      let imageSize = 560;
      if (window.innerWidth < 768) {
        imageSize = 370;
      }

      let productSke = form.querySelector(selector.productSke).cloneNode(true);
      var productUrl = product.url;

      let imageHtml = `<img src="${self.modifyImageUrl(product.imgUrl, imageSize)}" alt="${product.title}" class="img-fit card__main-image">`;
      let secondImage = product.imgUrl;
      let secondImageClass = 'is-main-image';
      if (product.extraData && product.extraData.altImage) {
        secondImage = product.extraData.altImage;
        secondImageClass = 'is-second-image';
      }
      imageHtml += `<img src="${self.modifyImageUrl(secondImage, imageSize)}" alt="${product.title}" class="img-fit card__hover-image ${secondImageClass}" fetchpriority="low">`;
      productSke.querySelector('.card__media > a').innerHTML = imageHtml;

      productSke.querySelector('.card__media a').setAttribute('href', productUrl);
      productSke.querySelector('.card__title a').innerText = product.title;
      productSke.querySelector('.card__title a').setAttribute('href', productUrl);
      productSke.querySelector('.card__vendor').innerText = product.brand ? product.brand : '';

      var price = parseFloat(product.price)
      var compareAtPrice = parseFloat(product.oldPrice)

      let priceFormatted = self.formatMoney(price * 100, window.theme.settings.moneyFormat);
      // if (product.extraData.isFromPrice == 'true') {
      //   priceFormatted = window.theme.strings.fromPrice + ' ' + priceFormatted;
      // }

      let compareAtPriceFormatted = self.formatMoney(compareAtPrice * 100, window.theme.settings.moneyFormat);
      productSke.querySelector('.price__current').innerText = priceFormatted;
      if (price < compareAtPrice) {
        productSke.querySelector('.price').classList.add('price--on-sale');
        productSke.querySelector('.price__was').innerText = compareAtPriceFormatted;

        let salePercent = Math.round((compareAtPrice - price) / compareAtPrice * 100);
        if (salePercent > 0 && productSke.querySelector('.badge--on-sale')) {
          productSke.querySelector('.badge--on-sale .badge-text').innerText += ` ${salePercent}% ${window.theme.strings.discount}`;
          productSke.querySelector('.badge--on-sale').classList.remove('hidden');
        }
      }

      if (!product.inStock && productSke.querySelector('.badge--sold-out')) {
        productSke.querySelector('.badge--sold-out').classList.remove('hidden');
      }

      // add new badge base on published date
      if (productSke.querySelector('.badge--new')) {
        let showNewBadge = false;

        /*
        let publishedDate = product.extraData.publishedAt // "2022-02-04T10:53:00+01:00"
        // check if publishedDate < 30 days ago
        let publishedDateObj = new Date(publishedDate);
        let diffTime = Math.abs(currentDate - publishedDateObj);
        let diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays < 30) {
          showNewBadge = true;
        }
        */
        
        const productTags = product.extraData.tags;
        if (productTags) {
          const productTagItems = productTags.toLowerCase().split(',');
          if (productTagItems.includes('new')) {
            showNewBadge = true;
          }
        }

        if (showNewBadge) {
          productSke.querySelector('.badge--new').classList.remove('hidden');
        }
      }

      if (product.extraDataList && product.extraDataList.custom_badges && product.extraDataList.custom_badges.length && productSke.querySelector('.badge--custom')) {
        // productSke.querySelector('.badge--custom .badge-text').innerText = product.extraDataList.badges[0];
        // productSke.querySelector('.badge--custom').classList.remove('hidden');
        let badgeArr = [];
        product.extraDataList.custom_badges.forEach(function (badge) {
          if (badge.indexOf('[') > -1 && badge.indexOf(']') > -1) {
            // parse array
            let parsedBadge = JSON.parse(badge);
            if (parsedBadge.length) {
              badgeArr = badgeArr.concat(parsedBadge);
            }
          } else {
            badgeArr.push(badge);
          }
        });
        // unique badgeArr
        badgeArr = [...new Set(badgeArr)];
        badgeArr.forEach(function (badge) {
          let style = 'background-color : #000000; color : #FFFFFF;';
          if (badge.toLowerCase().indexOf('limited') > -1) {
            style = 'background-color : #24342D; color : #FFFFFF;';
          }
          if (badge.toLowerCase().indexOf('exclusive') > -1) {
            style = 'background-color : #000000; color : #FFFFFF;';
          }
          let badgeHtml = `<div class="product-label-wrapper flex">
              <span class="product-label product-label--custom" style="${style}">
                ${badge}
              </span>
            </div>`;
          productSke.querySelector('.product-label-container').innerHTML += badgeHtml;
        });
      }
      if (product.extraData && product.extraData.custom_plus_price == 'true') {
        let plusPriceText = window.theme.strings.plusPrice;
        let badgePlusPriceHtml = `<div class="product-label-wrapper flex">
            <span class="product-label product-label--custom" style="background-color : #DFD6B5; color : #09090B;">
              ${plusPriceText}
            </span>
          </div>`;
        productSke.querySelector('.product-label-container').innerHTML += badgePlusPriceHtml;
      }

      productSke.querySelector('.card__quick-add .js-quick-add')?.setAttribute('data-product-url', productUrl);
      productSke.querySelector('.card__quick-add .quick-add-view-btn')?.setAttribute('href', productUrl);

      if (product.extraDataList?.strictStringifiedVariantData?.length) {
        let variants = product.extraDataList.strictStringifiedVariantData;
        // build the same card__stock-status as in product-card.liquid
        let variantHtml = '';
        let total = 0;
        let inventory_policy = '';
        variants.forEach(function (variant) {
          let variantData = JSON.parse(variant);

          let custom_inventory_kobenhavn = 0;
          let custom_inventory_horsens_lager = 0;
          let custom_inventory_aarhus = 0;
          let custom_inventory_horsens = 0;

          if (window.theme.settings.hrEnableKobenhavnLocation && variantData.metafields.custom_inventory_kobenhavn !== undefined) {
            custom_inventory_kobenhavn = parseInt(variantData.metafields.custom_inventory_kobenhavn.value);
          }
          if (window.theme.settings.hrEnableHorsensLagerLocation && variantData.metafields.custom_inventory_horsens_lager !== undefined) {
            custom_inventory_horsens_lager = parseInt(variantData.metafields.custom_inventory_horsens_lager.value);
          }
          if (window.theme.settings.hrEnableAarhusLocation && variantData.metafields.custom_inventory_aarhus !== undefined) {
            custom_inventory_aarhus = parseInt(variantData.metafields.custom_inventory_aarhus.value);
          }
          if (window.theme.settings.hrEnableHorsensLocation && variantData.metafields.custom_inventory_horsens !== undefined) {
            custom_inventory_horsens = parseInt(variantData.metafields.custom_inventory_horsens.value);
          }

          // total += variantData.inventory_quantity > 0 ? variantData.inventory_quantity : 0;
          total = custom_inventory_kobenhavn + custom_inventory_horsens_lager + custom_inventory_aarhus + custom_inventory_horsens; // change to this to ignore the inventory_quantity of the horsens location

          if (variantData.inventory_policy === 'continue') {
            inventory_policy = 'continue';
          }
        });

        let inventory_level;
        if (total <= 0) {
          if (inventory_policy === 'continue') {
            if (window.theme.settings.hide_no_stock_backordered) {
              inventory_level = 'hide';
            } else {
              inventory_level = 'backordered';
            }
          } else if (product.inStock) {
            inventory_level = 'in_stock';
          } else {
            inventory_level = 'hide';
          }
        } else if (total <= window.theme.settings.inventory_threshold_very_low) {
          inventory_level = 'very_low';
        } else if (total <= window.theme.settings.inventory_threshold_low) {
          inventory_level = 'low';
        } else {
          inventory_level = 'normal';
        }

        if (inventory_level === "normal" || inventory_level === "in_stock") {
          if (window.theme.settings.inventory_show_notice === "low") {
            inventory_level = 'in_stock';
          }
        }

        if (inventory_level !== 'hide') {
          variantHtml = `<div class="flex flex-col md:flex-row justify-between items-start md:items-center md:mr-2"><div class="card__stock-status flex items-center mb-1 text-xs gap-1" data-inventory-level="${inventory_level}">`;
          if (inventory_level === 'very_low') {
            variantHtml += `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12Z" fill="#2c7e3f"></path>
              <path d="M6 9C7.65685 9 9 7.65685 9 6C9 4.34315 7.65685 3 6 3C4.34315 3 3 4.34315 3 6C3 7.65685 4.34315 9 6 9Z" fill="white"></path>
              </svg>`;
            variantHtml += `<span class="ml-1">${window.theme.strings.veryLowStock}`;
            if (window.theme.settings.inventory_show_count !== "never" && total > 0) {
              variantHtml += ` (${total} ${window.theme.strings.available})`;
            }
            variantHtml += `</span>`;
          } else if (inventory_level === 'low') {
            variantHtml += `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12Z" fill="#2c7e3f"></path>
              <path d="M6 9C7.65685 9 9 7.65685 9 6C9 4.34315 7.65685 3 6 3C4.34315 3 3 4.34315 3 6C3 7.65685 4.34315 9 6 9Z" fill="white"></path>
              </svg>`;
            variantHtml += `<span class="ml-1">${window.theme.strings.lowStock}`;
            if (window.theme.settings.inventory_show_count !== "never" && total > 0) {
              variantHtml += ` (${total} ${window.theme.strings.available})`;
            }
            variantHtml += `</span>`;
          } else if (inventory_level === 'backordered') {
            variantHtml += `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12Z" fill="#2c7e3f"></path>
              <path d="M6 9C7.65685 9 9 7.65685 9 6C9 4.34315 7.65685 3 6 3C4.34315 3 3 4.34315 3 6C3 7.65685 4.34315 9 6 9Z" fill="white"></path>
              </svg>`;
            variantHtml += `<span class="ml-1">${window.theme.strings.backordered}</span>`;
          } else {
            variantHtml += `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 12C9.31371 12 12 9.31371 12 6C12 2.68629 9.31371 0 6 0C2.68629 0 0 2.68629 0 6C0 9.31371 2.68629 12 6 12Z" fill="#2c7e3f"></path>
              <path d="M6 9C7.65685 9 9 7.65685 9 6C9 4.34315 7.65685 3 6 3C4.34315 3 3 4.34315 3 6C3 7.65685 4.34315 9 6 9Z" fill="white"></path>
              </svg>`;
            variantHtml += `<span class="ml-1">${window.theme.strings.inStock}`;
            if (window.theme.settings.inventory_show_count === "always" && total > 0) {
              variantHtml += ` (${total} ${window.theme.strings.available})`;
            }
            variantHtml += `</span>`;
          }
          variantHtml += `</div>`;
          if (variants.length > 1) {
            let moreVariantsBtn = `<button type="button" class="js-quick-add relative z-10 mb-2 md:mb-1 text-left" aria-haspopup="dialog" data-product-url="${productUrl}" data-product-default-variant="false" data-quick-add-listener-added="true">
              + ${window.theme.strings.moreVariants} (${variants.length})
            </button>`;
            variantHtml += moreVariantsBtn;
          }
          variantHtml += `</div>`;
          productSke.querySelector('.card__info-inner').insertAdjacentHTML('afterbegin', variantHtml);
        }

        if (variants.length == 1 && product.inStock) {
          // console.log(JSON.parse(variants[0]));
          let singleVariantId = JSON.parse(variants[0]).id;
          if (singleVariantId) {
            productSke.querySelector('.js-quick-add').setAttribute('data-variant-id', singleVariantId);
            productSke.querySelector('.js-quick-add').classList.add('js-quick-add-variant');
          }
        }

        
      }

      return productSke;
    }

    modifyImageUrl(imageUrl, width) {
      if (!imageUrl) return imageUrl;
      // Only resize Shopify CDN URLs
      if (!imageUrl.includes("cdn.shopify.com")) {
        return imageUrl;
      }

      const [path, query] = imageUrl.split('?');

      // Skip URLs that already contain a Shopify size suffix (e.g. `_600x.webp`
      // or `_600x600.webp`) before the extension. Re-applying a size would
      // produce a path like `..._600x_560x560.webp` which 404s on the CDN.
      if (/_\d+x\d*\.[^./]+$/.test(path)) {
        return imageUrl;
      }

      const newPath = path.replace(/(\.[^.]*)$/, `_${width}x${width}$1`);
      return query ? `${newPath}?${query}` : newPath;
    }

    /**
     * Render the filter from Hello Retail
     */
    async _renderSearchFilters(response) {
      if (response.filters) {
        let self = this;
        let data = response;
        let form = this._searchForm;
        let selector = this.selector;
        let filtersWrapper = form.querySelector(selector.filtersWrapper);
        filtersWrapper.innerHTML = '';
        let allFilters = data.filters;
        let filterOrder = [];
        // sort allFilters by filterOrder, if any filter inside allFilters is not in filterOrder, then push it to the end
        let sortedFilters = {};
        filterOrder.forEach(function (key) {
          if (allFilters[key]) {
            sortedFilters[key] = allFilters[key];
            delete allFilters[key];
          }
        });
        // add the rest of filters to the end
        for (let key in allFilters) {
          if (allFilters.hasOwnProperty(key)) {
            sortedFilters[key] = allFilters[key];
          }
        }
        for (let key in sortedFilters) {
          if (sortedFilters.hasOwnProperty(key)) {
            let filterTitle = key;

            for (let key2 in data.filter_titles) {
              if (data.filter_titles.hasOwnProperty(key2)) {
                if (key2 === key) {
                  filterTitle = data.filter_titles[key2];
                }
              }
            };

            let filterSke = form.querySelector(selector.filterSke).cloneNode(true);
            filterSke.querySelector('.hr-filter').setAttribute('data-filter', key);
            filterSke.querySelector('.hr-filter-heading').innerText = filterTitle;
            // if (window.theme.settings.helloretailSearchOnFilterList && window.theme.settings.helloretailSearchOnFilterList.length) {
              // if (window.theme.settings.helloretailSearchOnFilterList.includes(key) && filterSke.querySelector('.search-facet-search')) {
              if (filterSke.querySelector('.search-facet-search') && key != 'price') {
                filterSke.querySelector('.search-facet-search').classList.remove('hidden');
              }
            // }

            let dropdownContent = '';
            if (key == 'price') {
              //dropdownContent = `<p data-min="${sortedFilters[key].min}" data-max="${sortedFilters[key].max}">range</p>`;
              // let minPrice = Math.floor(parseFloat(sortedFilters[key].min));
              let maxPrice = Math.floor(parseFloat(sortedFilters[key].max));
              let selectedMin = 0;
              let selectedMax = maxPrice;
              if (sortedFilters[key].selectedMin) {
                selectedMin = Math.floor(parseFloat(sortedFilters[key].selectedMin));
              }
              if (sortedFilters[key].selectedMax) {
                selectedMax = Math.floor(parseFloat(sortedFilters[key].selectedMax));
              }
              if (sortedFilters[key].selectedMin || sortedFilters[key].selectedMax) {
                filterSke.querySelector('.hr-filter-heading').innerHTML += `<span>1</span>`;
              }

              let priceSke = form.querySelector(selector.priceSke).cloneNode(true);
              priceSke.querySelector('.range-min').setAttribute('max', maxPrice);
              priceSke.querySelector('.range-min').setAttribute('value', selectedMin);
              priceSke.querySelector('.range-max').setAttribute('max', maxPrice);
              priceSke.querySelector('.range-max').setAttribute('value', selectedMax);
              priceSke.querySelector('.field-price-min').setAttribute('max', maxPrice);
              priceSke.querySelector('.field-price-min').setAttribute('value', selectedMin);
              priceSke.querySelector('.field-price-min').setAttribute('placeholder', 0);
              priceSke.querySelector('.field-price-max').setAttribute('max', maxPrice);
              priceSke.querySelector('.field-price-max').setAttribute('value', selectedMax);
              priceSke.querySelector('.field-price-max').setAttribute('placeholder', maxPrice);
              dropdownContent = `<price-range class="price-range">${priceSke.innerHTML}</price-range>`;
            } else {
              let filterArr = sortedFilters[key];
              if (key == 'extraDataList.PIMSizeSizeList') {
                // sort filter values by XXXS, XXS, XS, S, M, L, XL, XXL, XXXL, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9..
                filterArr.sort((a, b) => {
                  let keyA = self.sizeSortKey(a.title);
                  let keyB = self.sizeSortKey(b.title);
    
                  if (keyA.category !== keyB.category) {
                    return keyA.category - keyB.category;
                  } else if (keyA.value !== keyB.value) {
                    return keyA.value > keyB.value ? 1 : -1;
                  }
                  return 0;
                });
              } else if (key == 'extraDataList.PIMSizeAgeAgeList') {
                const config = {
                  monthUnit: window.theme.strings.helloretailMonthUnit || "mdr",
                  yearUnit: window.theme.strings.helloretailYearUnit || "år",
                  firstItem: window.theme.strings.helloretailFirstItem || "Præmature",
                  lastItem: window.theme.strings.helloretailLastItem || "Voksen"
                };

                filterArr.sort((a, b) => {
                  const aTitle = a.title
                  const bTitle = b.title
                  if (aTitle === config.firstItem) return -1;
                  if (bTitle === config.firstItem) return 1;
                  if (aTitle === config.lastItem) return 1;
                  if (bTitle === config.lastItem) return -1;

                  let aValue = parseInt(aTitle, 10);
                  let bValue = parseInt(bTitle, 10);
                  let aUnit = aTitle.indexOf(config.monthUnit) > -1 ? config.monthUnit : config.yearUnit;
                  let bUnit = bTitle.indexOf(config.monthUnit) > -1 ? config.monthUnit : config.yearUnit;

                  if (aUnit === bUnit) {
                    return aValue - bValue;
                  }
                  return aUnit === config.monthUnit ? -1 : 1;
                });
              } else {
                // sort filter values by alphabet
                filterArr.sort(function(a, b) {
                  let titleA = a.title.toUpperCase();
                  let titleB = b.title.toUpperCase();
                  if (titleA < titleB) {
                    return -1;
                  }
                  if (titleA > titleB) {
                    return 1;
                  }
                  return 0;
                });
              }

              let currentFiltersCount = 0;
              filterArr.forEach(function(filter) {
                if (filter.selected) {
                  currentFiltersCount++;
                }
                let filterTitle = filter.title;
                if (filterTitle.toLowerCase() == 'true') {
                  filterTitle = window.theme.strings.filterOptionTrue;
                } else if (filterTitle.toLowerCase() == 'false') {
                  filterTitle = window.theme.strings.filterOptionFalse;
                }
                if (key == 'hierarchies' && filterTitle.endsWith('$')) {
                  filterTitle = filterTitle.replace(/.$/g, '');
                }
                dropdownContent += `<li data-query="${filter.query}" class="${filter.selected?'selected':''}">${filterTitle}</li>`;
              });
              if (currentFiltersCount) {
                filterSke.querySelector('.hr-filter-heading').innerHTML += `<span>${currentFiltersCount}</span>`;
              }
            }
            filterSke.querySelector('.hr-filter-dropdown-content').innerHTML += `<ul>${dropdownContent}</ul>`;
            filtersWrapper.appendChild(filterSke);

            // filter when submit price range
            form.querySelector('.hr-price-submit').addEventListener('click', function(e) {
              e.preventDefault();
              form.querySelector('.hr-price-submit').classList.add('loading');
              self.getResultsFromHelloRetail(self.getQuery());
            });

            // toggle filter dropdown when click on filter heading
            filterSke.querySelector('.hr-filter .hr-filter-heading').addEventListener('click', function(e) {
              e.preventDefault();
              e.target.closest('.hr-filter').classList.toggle('active');
              // remove class active from other filters
              form.querySelectorAll('.hr-filter').forEach(function(filter) {
                if (filter !== e.target.closest('.hr-filter')) {
                  filter.classList.remove('active');
                }
              });
            });

            // do filter when click on filter item
            filterSke.querySelectorAll('.hr-filter-dropdown-content li').forEach(function(filterItem) {
              filterItem.addEventListener('click', function(e) {
                e.preventDefault();
                this.classList.toggle('selected');
                self.getResultsFromHelloRetail(self.getQuery());
              });
            });

            // search inside filter
            if (filterSke.querySelector('.search-facet-search')) {
              filterSke.querySelector('.search-facet-search').addEventListener('input', debounce(function(e) {
                let searchValue = e.target.value.toLowerCase();
                filterSke.querySelectorAll('.hr-filter-dropdown-content li').forEach(function(filterItem) {
                  if (filterItem.innerText.toLowerCase().indexOf(searchValue) > -1) {
                    filterItem.classList.remove('hidden');
                  } else {
                    filterItem.classList.add('hidden');
                  }
                });
              }), 200);
            }
          }
        }
        // append sort dropdown
        let sortSke = form.querySelector('.hr-sort-skeleton li').cloneNode(true);

        filtersWrapper.appendChild(sortSke);

        if (self.currentSearchSort.length) {
          sortSke.querySelectorAll('.hr-search-sort-option').forEach(function(sortItem) {
            if (self.currentSearchSort.includes(sortItem.getAttribute('data-value'))) {
              sortItem.classList.add('selected');
            }
          });
        } else {
          sortSke.querySelector('.hr-search-sort-option[data-value="most-popular"]').classList.add('selected');
        }
        
        // toggle filter dropdown when click on filter heading
        sortSke.querySelector('.hr-filter .hr-filter-heading').addEventListener('click', function(e) {
          e.preventDefault();
          e.target.closest('.hr-filter').classList.toggle('active');
          // remove class active from other filters
          form.querySelectorAll('.hr-filter').forEach(function(filter) {
            if (filter !== e.target.closest('.hr-filter')) {
              filter.classList.remove('active');
            }
          });
        });

        // do filter when click on sort item
        sortSke.querySelectorAll('.hr-filter-dropdown-content li').forEach(function(sortItem) {
          sortItem.addEventListener('click', function(e) {
            e.preventDefault();
            // remove selected on siblings
            this.parentNode.querySelectorAll('li').forEach(function(sibling) {
              sibling.classList.remove('selected');
            });
            this.classList.add('selected');
            self.getResultsFromHelloRetail(self.getQuery());
          });
        });
      }
    }

    /**
     * Show/hide filters mobile
     */
    _toggleMobileFilters() {
      this.querySelector('.hr-filters')?.classList.toggle('active');
      this.querySelector('.hr-filters-toggle-mobile')?.classList.toggle('active');
    }

    /**
     * Toggle mobile categories show more/less
     */
    _toggleMobileCategories(type) {
      const form = this._searchForm;
      const selector = this.selector;
      if (!form || !selector) return;
      const categoriesWrapper = form.querySelector(selector.categoriesWrapper);
      const pagesWrapper = form.querySelector(selector.pagesWrapper);
      const showMoreText = form.querySelector(selector.showMoreText);
      const showLessText = form.querySelector(selector.showLessText);
      const pageShowMoreText = form.querySelector(selector.pageShowMoreText);
      const pageShowLessText = form.querySelector(selector.pageShowLessText);
      
      const categoryItems = categoriesWrapper.querySelectorAll('li');
      const pageItems = pagesWrapper.querySelectorAll('li');

      this.categoriesExpanded = !this.categoriesExpanded;
      this.pagesExpanded = !this.pagesExpanded;

      if (type == 'categories') {
        if (this.categoriesExpanded) {
          // Show all categories
          categoryItems.forEach((item, index) => {
            if (index >= 4) {
              item.classList.remove('hidden');
            }
          });
          showMoreText.classList.add('hidden');
          showLessText.classList.remove('hidden');
        } else {
          // Hide categories beyond the first 4
          categoryItems.forEach((item, index) => {
            if (index >= 4) {
              item.classList.add('hidden');
            }
          });
          showMoreText.classList.remove('hidden');
          showLessText.classList.add('hidden');
        }
      }
      if (type == 'pages') {
        if (this.pagesExpanded) {
          // Show all pages
          pageItems.forEach((item, index) => {
            if (index >= 4) {
              item.classList.remove('hidden');
            }
          });
          pageShowMoreText.classList.add('hidden');
          pageShowLessText.classList.remove('hidden');
        } else {
          // Hide pages beyond the first 4
          pageItems.forEach((item, index) => {
            if (index >= 4) {
              item.classList.add('hidden');
            }
          });
          pageShowMoreText.classList.remove('hidden');
          pageShowLessText.classList.add('hidden');
        }
      }
    }

    formatMoney(cents, format = "") {
      if (typeof cents === "string") {
        cents = cents.replace(".", "");
      }
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format;
      function defaultTo(value2, defaultValue) {
        return value2 == null || value2 !== value2 ? defaultValue : value2;
      }
      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = defaultTo(precision, 2);
        thousands = defaultTo(thousands, ",");
        decimal = defaultTo(decimal, ".");
        if (isNaN(number) || number == null) {
          return 0;
        }
        number = (number / 100).toFixed(precision);
        let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
        return dollarsAmount + centsAmount;
      }
      let value = "";
      switch (formatString.match(placeholderRegex)[1]) {
        case "amount":
          value = formatWithDelimiters(cents, 2);
          break;
        case "amount_no_decimals":
          value = formatWithDelimiters(cents, 0);
          break;
        case "amount_with_space_separator":
          value = formatWithDelimiters(cents, 2, " ", ".");
          break;
        case "amount_with_comma_separator":
          value = formatWithDelimiters(cents, 2, ".", ",");
          break;
        case "amount_with_apostrophe_separator":
          value = formatWithDelimiters(cents, 2, "'", ".");
          break;
        case "amount_no_decimals_with_comma_separator":
          value = formatWithDelimiters(cents, 0, ".", ",");
          break;
        case "amount_no_decimals_with_space_separator":
          value = formatWithDelimiters(cents, 0, " ");
          break;
        case "amount_no_decimals_with_apostrophe_separator":
          value = formatWithDelimiters(cents, 0, "'");
          break;
      }
      if (formatString.indexOf("with_comma_separator") !== -1) {
        return formatString.replace(placeholderRegex, value);
      } else {
        return formatString.replace(placeholderRegex, value);
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
